import React, { useState, useMemo } from "react";
import styles from "./subscription-overview-section.module.scss";
import DonutChart from "../../../Common/donut-chart";

function getColorValue(colorName) {
  const colors = {
    primary: "#008FD4",
    orange: "#FF6B35",
    success: "#319F6B",
    warning: "#FFAC2C",
    gray: "#616778",
    churned: "#E74C3C"
  };
  return colors[colorName] || colors.primary;
}

export const SubscriptionOverviewSection = ({ 
  statsConfig = [], 
  isDistributor = true,
  distributorsData = []
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const { total, graphData, legendData } = useMemo(() => {
    const totalValue = statsConfig.reduce((sum, stat) => sum + (stat.value || 0), 0);
    
    const colorMap = {
      "Active": "primary",
      "Suspended": "orange", 
      "Active trials": "success",
      "Expired Trial": "warning",
      "Paid accounts": "gray",
      "Overall Accounts": "churned"
    };

    const graphData = statsConfig
      .filter(stat => stat.value > 0)
      .map(stat => ({
        label: stat.title,
        value: stat.value,
        colorId: colorMap[stat.title] || "primary",
        color: getColorValue(colorMap[stat.title] || "primary")
      }));

    const legendData = statsConfig.map(stat => ({
      label: stat.title,
      value: stat.value,
      color: colorMap[stat.title] || "primary"
    }));

    return { total: totalValue, graphData, legendData };
  }, [statsConfig]);

  if (isDistributor && distributorsData && distributorsData.length > 0) {
    return (
      <section className={styles.section}>
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>Subscription Overview</h2>
          </div>
        </header>
        <div className={styles.distributorContainer}>
          <div className={styles.distributorChartWrapper}>
            <div className={styles.distributorChartInner}>
              <div className={styles.distributorChartBox}>
                <div className={styles.chartContent}>
                  <DonutChart
                    width={160}
                    graphData={graphData}
                    thickness={35}
                    className={styles.donutChart}
                    emptyStateText={total.toString()}
                    onHoverChange={setIsHovered}
                  />
                  {!isHovered && <div className={styles.chartNumber}>{total}</div>}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.distributorTableContainer}>
            <div className={styles.distributorTableHeader}>
              <div className={styles.tableHeaderCell}>Distributor</div>
              <div className={styles.tableHeaderCell}>MSPs</div>
              <div className={styles.tableHeaderCell}>Revenue</div>
            </div>
            <ul className={styles.distributorList}>
              {distributorsData.slice(0, 10).map((item, index) => (
                <li key={index} className={styles.distributorListItem}>
                  <div className={styles.legendItemContent}>
                    <div className={`${styles.legendDot} ${styles[item.color || 'primary']}`} />
                    <div className={styles.legendLabel}>{item.name || item.Label}</div>
                  </div>
                  <div className={styles.legendValue}>{item.mspCount || item.Value || 0}</div>
                  <div className={styles.legendValue}>{item.revenue || '€ 0.00'}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h2 className={styles.title}>Subscription Overview</h2>
        </div>
      </header>
      <div className={styles.chartContainer}>
        <div className={styles.chartWrapper}>
          <div className={styles.chartInner}>
            <div className={styles.chartBox}>
              <div className={styles.chartContent}>
                <DonutChart
                  width={160}
                  graphData={graphData}
                  thickness={35}
                  className={styles.donutChart}
                  emptyStateText={total.toString()}
                  onHoverChange={setIsHovered}
                />
                {!isHovered && <div className={styles.chartNumber}>{total}</div>}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.legendContainer}>
          {legendData.map((item, index) => (
            <div key={index} className={styles.legendItem}>
              <div className={styles.legendItemContent}>
                <div className={`${styles.legendDot} ${styles[item.color]}`} />
                <div className={styles.legendLabel}>{item.label}</div>
              </div>
              <div className={styles.legendValue}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
